import { ThemeProvider, withStyles } from '@material-ui/styles';
import React, { useCallback } from 'react';
import { TextField, Tooltip } from '@material-ui/core';
import { connect } from 'react-redux';
import flowRight from 'lodash.flowright';

import styles, { inputTheme } from '../styles/informationSection';
import Optional from '../../../utils/optional';
import AvailableNumbers from './AvailableNumbers';
import PhoneLine from './PhoneLine';
import { translate } from '../../../i18n/I18nProvider';
import clsx from 'clsx';
import withSettings from '../../../utils/withSettings';

const PhoneNumbers = ({
  classes,
  form,
  availableNumbers,
  numberErrors,
  setNumberErrors,
  errors = {},
  setFieldError,
  setFieldTouched,
  validateForm,
  settings
}) => {
  const {
    values: { phoneNumbers = {} },
    setFieldValue,
    initialValues: { phoneNumbers: initialPhoneNumbers = {} }
  } = form;

  const handleLineChange = useCallback(
    ({ field, value, line, phoneLine }) => {
      const updatedLine = { ...phoneLine, [field]: value };
      setFieldValue('phoneNumbers', {
        ...phoneNumbers,
        [line]: { ...updatedLine }
      });
    },
    [setFieldValue, phoneNumbers]
  );

  const handleTextfieldBlur = useCallback(
    ({ line, isAlias, index }) => {
      if (isAlias) {
        delete numberErrors[line]?.aliases?.[index];
        setNumberErrors({ ...numberErrors });
        const alias = phoneNumbers[line]?.aliases.find(el => el.id === index);

        const exists = availableNumbers.some(n => n.value === alias?.value);

        if (!exists && alias?.value) {
          setNumberErrors({
            ...numberErrors,
            [`${line}_alias_${index}`]: 'This number is not available'
          });
        }
      } else {
        delete numberErrors[line];
        setNumberErrors({ ...numberErrors });
        const exists =
          availableNumbers.some(n => n.value === phoneNumbers[line]?.value) ||
          phoneNumbers[line]?.value === initialPhoneNumbers[line]?.value;

        if (!exists && phoneNumbers[line]?.value) {
          setNumberErrors({
            ...numberErrors,
            [line]: 'This number is not available'
          });
        } else {
          delete numberErrors[line];
          setNumberErrors({ ...numberErrors });
        }
      }
    },
    // eslint-disable-next-line
    [phoneNumbers, availableNumbers]
  );

  const getExcludedNumbers = useCallback(
    currentLine => {
      const excludedNumbers = [];

      Object.keys(phoneNumbers).forEach(lineKey => {
        if (lineKey !== currentLine && phoneNumbers[lineKey]?.value) {
          excludedNumbers.push(phoneNumbers[lineKey].value);
        }

        if (lineKey !== currentLine && phoneNumbers[lineKey]?.aliases) {
          phoneNumbers[lineKey].aliases.forEach(alias => {
            if (alias.value) {
              excludedNumbers.push(alias.value);
            }
          });
        }
      });

      return excludedNumbers;
    },
    [phoneNumbers]
  );

  const setSelectedNumber = useCallback(
    (selectedNumber, line) => {
      const numberValue =
        typeof selectedNumber === 'string'
          ? selectedNumber
          : selectedNumber?.value || selectedNumber;

      return setFieldValue('phoneNumbers', {
        ...phoneNumbers,
        [line]: { ...phoneNumbers[line], value: numberValue }
      });
    },
    [setFieldValue, phoneNumbers]
  );

  const handleClearFaxLine = useCallback(() => {
    setFieldValue('phoneNumbers', {
      ...phoneNumbers,
      faxLine: {}
    });
  }, [setFieldValue, phoneNumbers]);

  return (
    <div className={classes.root}>
      <div className={classes.label}>{translate('USER.PERSONAL_LINES')}</div>
      <div className={classes.container}>
        <PhoneLine
          line="primaryLine"
          label={`${translate('PRIMARY')} ${translate('LINE').toLowerCase()}`}
          handleChange={handleLineChange}
          handleTextfieldBlur={handleTextfieldBlur}
          numberError={numberErrors}
          setNumberErrors={setNumberErrors}
          setSelectedNumber={setSelectedNumber}
          phoneLine={phoneNumbers.primaryLine || {}}
          excludeNumbers={getExcludedNumbers('primaryLine')}
          validationErrors={errors?.phoneNumbers?.primaryLine}
          setFieldError={setFieldError}
          setFieldTouched={setFieldTouched}
          validateForm={validateForm}
        />
        <PhoneLine
          line="secondaryLine"
          label={`${translate('SECONDARY')} ${translate('LINE').toLowerCase()}`}
          handleChange={handleLineChange}
          handleTextfieldBlur={handleTextfieldBlur}
          numberError={numberErrors}
          setNumberErrors={setNumberErrors}
          setSelectedNumber={setSelectedNumber}
          phoneLine={phoneNumbers.secondaryLine || {}}
          excludeNumbers={getExcludedNumbers('secondaryLine')}
          validationErrors={errors?.phoneNumbers?.secondaryLine}
          setFieldError={setFieldError}
          setFieldTouched={setFieldTouched}
          validateForm={validateForm}
        />
        <div className={classes.numberContainer}>
          <div className={classes.smallLabel}>{translate('FAX_NUMBER')}</div>
          <div className={classes.numberRow}>
            <div className={classes.box}>
              <div>{translate('TABLE.NUMBER')}</div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  value={Optional(phoneNumbers?.faxLine?.value).or('')}
                  onChange={e =>
                    handleLineChange({
                      field: 'value',
                      value: e.target.value,
                      line: 'faxLine'
                    })
                  }
                  onBlur={() =>
                    handleTextfieldBlur({
                      line: 'faxLine'
                    })
                  }
                  error={!!numberErrors?.faxLine}
                  helperText={numberErrors?.faxLine}
                  style={{ flex: 1 }}
                  disabled
                  InputProps={{
                    style: {
                      color: 'inherit'
                    }
                  }}
                />
                {phoneNumbers?.faxLine?.value && (
                  <Tooltip title={translate('USER.CLEAR_NUMBER')}>
                    <i
                      style={{
                        color: settings?.colors.modal_header,
                        cursor: 'pointer',
                        marginLeft: '8px'
                      }}
                      className={clsx('fas fa-trash-alt', classes.deleteIcon)}
                      onClick={handleClearFaxLine}
                    />
                  </Tooltip>
                )}
              </div>

              <AvailableNumbers
                setSelectedNumber={selected => {
                  const numberValue =
                    selected?.value || selected?.number || selected;
                  setSelectedNumber(numberValue, 'faxLine');
                }}
                excludeNumbers={getExcludedNumbers('faxLine')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStatesToProps = ({ editUser }) => {
  return {
    availableNumbers: editUser.availableNumbers || []
  };
};

export default flowRight(
  withSettings,
  connect(mapStatesToProps),
  withStyles(styles)
)(PhoneNumbers);
